/**
 * Date and Time Utility Functions
 *
 * This module provides consistent date/time handling across the application.
 * All dates are stored in UTC on the backend and converted to local time for display.
 *
 * Key principles:
 * 1. Backend stores and expects UTC ISO strings (e.g., "2026-03-14T22:00:00Z")
 * 2. Frontend displays times in user's local timezone
 * 3. date-fns parseISO() automatically converts UTC to local Date objects
 * 4. Always use these utilities for consistency
 */

import {
  format,
  parseISO,
  formatISO,
  startOfDay as fnsStartOfDay,
  endOfDay as fnsEndOfDay,
  isWithinInterval,
  isSameDay,
  isBefore,
  isAfter,
  addMinutes
} from 'date-fns';

/**
 * Parse a UTC ISO string from backend to a local Date object
 * @param {string} isoString - UTC ISO string (e.g., "2026-03-14T22:00:00Z")
 * @returns {Date} Local Date object
 *
 * @example
 * parseUTCString("2026-03-14T22:00:00Z")
 * // Returns Date object: Mar 14, 2026 2:00 PM PST (if in PST timezone)
 */
export const parseUTCString = (isoString) => {
  if (!isoString) return null;
  return parseISO(isoString);
};

/**
 * Convert a local Date object to UTC ISO string for backend
 * @param {Date} date - Local Date object
 * @returns {string} UTC ISO string
 *
 * @example
 * toUTCString(new Date(2026, 2, 14, 14, 0)) // Mar 14, 2026 2:00 PM local
 * // Returns "2026-03-14T22:00:00.000Z" (if in PST timezone)
 */
export const toUTCString = (date) => {
  if (!date) return null;
  return formatISO(date);
};

/**
 * Format a date for display
 * @param {Date|string} date - Date object or ISO string
 * @param {string} formatString - Format string (see date-fns format)
 * @returns {string} Formatted date string
 *
 * @example
 * formatDate("2026-03-14T22:00:00Z", "MMM d, yyyy h:mm a")
 * // Returns "Mar 14, 2026 2:00 PM" (in PST)
 */
export const formatDate = (date, formatString = 'MMM d, yyyy h:mm a') => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseUTCString(date) : date;
  return format(dateObj, formatString);
};

/**
 * Get start of day in UTC for backend filtering
 * @param {Date} date - Local Date object
 * @returns {string} UTC ISO string at start of day
 *
 * @example
 * getStartOfDayUTC(new Date(2026, 2, 14)) // Mar 14, 2026 local
 * // Returns "2026-03-14T08:00:00.000Z" (in PST, which is midnight PST)
 */
export const getStartOfDayUTC = (date) => {
  if (!date) return null;
  const startOfDay = fnsStartOfDay(date);
  return formatISO(startOfDay);
};

/**
 * Get end of day in UTC for backend filtering
 * @param {Date} date - Local Date object
 * @returns {string} UTC ISO string at end of day
 *
 * @example
 * getEndOfDayUTC(new Date(2026, 2, 14)) // Mar 14, 2026 local
 * // Returns "2026-03-15T07:59:59.999Z" (in PST, which is 11:59:59 PM PST)
 */
export const getEndOfDayUTC = (date) => {
  if (!date) return null;
  const endOfDay = fnsEndOfDay(date);
  return formatISO(endOfDay);
};

/**
 * Check if an event falls on a specific date (local timezone)
 * @param {string} eventStartISO - Event start time (UTC ISO string)
 * @param {Date} date - Date to check against (local)
 * @returns {boolean}
 */
export const isEventOnDate = (eventStartISO, date) => {
  if (!eventStartISO || !date) return false;
  const eventStart = parseUTCString(eventStartISO);
  return isSameDay(eventStart, date);
};

/**
 * Check if an event falls within a time slot (15-minute granularity)
 * @param {string} eventStartISO - Event start time (UTC ISO string)
 * @param {Date} slotDate - Date of the slot (local)
 * @param {number} hour - Hour (0-23)
 * @param {number} minute - Minute (0, 15, 30, 45)
 * @returns {boolean}
 */
export const isEventInTimeSlot = (eventStartISO, slotDate, hour, minute) => {
  if (!eventStartISO || !slotDate) return false;

  const eventStart = parseUTCString(eventStartISO);

  // Create the time slot boundaries
  const slotStart = new Date(slotDate);
  slotStart.setHours(hour, minute, 0, 0);

  const slotEnd = addMinutes(slotStart, 15);

  // Check if event starts within this 15-minute slot
  return eventStart >= slotStart && eventStart < slotEnd;
};

/**
 * Get the duration of an event in minutes
 * @param {string} startISO - Event start time (UTC ISO string)
 * @param {string} endISO - Event end time (UTC ISO string)
 * @returns {number} Duration in minutes
 */
export const getEventDuration = (startISO, endISO) => {
  if (!startISO || !endISO) return 0;
  const start = parseUTCString(startISO);
  const end = parseUTCString(endISO);
  return Math.round((end - start) / 1000 / 60);
};

/**
 * Check if a date/time is in the past
 * @param {Date|string} date - Date object or ISO string
 * @returns {boolean}
 */
export const isPast = (date) => {
  if (!date) return false;
  const dateObj = typeof date === 'string' ? parseUTCString(date) : date;
  return isBefore(dateObj, new Date());
};

/**
 * Check if a date/time is in the future
 * @param {Date|string} date - Date object or ISO string
 * @returns {boolean}
 */
export const isFuture = (date) => {
  if (!date) return false;
  const dateObj = typeof date === 'string' ? parseUTCString(date) : date;
  return isAfter(dateObj, new Date());
};

/**
 * Check if an event is currently happening
 * @param {string} startISO - Event start time (UTC ISO string)
 * @param {string} endISO - Event end time (UTC ISO string)
 * @returns {boolean}
 */
export const isEventHappening = (startISO, endISO) => {
  if (!startISO || !endISO) return false;
  const now = new Date();
  const start = parseUTCString(startISO);
  const end = parseUTCString(endISO);
  return isWithinInterval(now, { start, end });
};

/**
 * Format time range for display
 * @param {string} startISO - Event start time (UTC ISO string)
 * @param {string} endISO - Event end time (UTC ISO string)
 * @param {boolean} showDate - Whether to include the date
 * @returns {string} Formatted time range
 *
 * @example
 * formatTimeRange("2026-03-14T22:00:00Z", "2026-03-14T23:00:00Z")
 * // Returns "2:00 PM - 3:00 PM" (in PST)
 *
 * formatTimeRange("2026-03-14T22:00:00Z", "2026-03-14T23:00:00Z", true)
 * // Returns "Mar 14, 2026 2:00 PM - 3:00 PM" (in PST)
 */
export const formatTimeRange = (startISO, endISO, showDate = false) => {
  if (!startISO || !endISO) return '';

  const start = parseUTCString(startISO);
  const end = parseUTCString(endISO);

  if (showDate) {
    if (isSameDay(start, end)) {
      // Same day: "Mar 14, 2026 2:00 PM - 3:00 PM"
      return `${format(start, 'MMM d, yyyy h:mm a')} - ${format(end, 'h:mm a')}`;
    } else {
      // Different days: "Mar 14, 2026 2:00 PM - Mar 15, 2026 3:00 PM"
      return `${format(start, 'MMM d, yyyy h:mm a')} - ${format(end, 'MMM d, yyyy h:mm a')}`;
    }
  }

  // Time only: "2:00 PM - 3:00 PM"
  return `${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`;
};
